import {
  GridTable,
  GridTableColumn,
  GridTableRecord,
} from '@apexinternal/ui-components'
import { PaginationProps } from '@apexinternal/ui-components/dist/components/GridTable/types'
import { Badge, Link } from '@chakra-ui/react'
import { capitalize, isEmpty, uniqueId } from 'lodash'
import React from 'react'
import { Link as ReactRouterLink } from 'react-router-dom'
import { EMPTY_ADDRESS } from '../../constants'
import { useAccountSearch } from '../../hooks'
import { Account } from '../../types'
import { AccountTypeEnum } from '../../types/AccountTypeEnum'
import { RoutesEnum } from '../../types/RoutesEnum'
import { formatAddress, formatPhoneNumbers } from '../../utils'

const ACCOUNT_STATUS_OPTIONS = [
  {
    value: 'true',
    label: 'Open',
  },
  {
    value: 'false',
    label: 'Closed',
  },
]
const ACCOUNT_TYPE_OPTIONS_EXCEPT_SHORT = [
  { value: AccountTypeEnum.Cash, label: 'Cash' },
  { value: AccountTypeEnum.Dvp, label: 'DVP' },
  { value: AccountTypeEnum.Margin, label: 'Margin' },
]

const assembleColumn = (
  label: string,
  recordField: string,
  width: number,
  options?: {
    value: string
    label: string
  }[],
  cellRenderer?: ({ value }: { value: any }) => JSX.Element
): GridTableColumn => ({
  key: recordField,
  label,
  recordField,
  isFilterable: true,
  isSortable: true,
  isVisibleByDefault: true,
  width,
  ...(!isEmpty(options) && { options }),
  ...(cellRenderer && { cellRenderer }),
})

export const AccountStatusCell = ({ value }: { value: any }) => {
  const isActive = JSON.parse(value.toLowerCase())
  return (
    <Badge
      variant="solid"
      padding="0.25rem 0.75rem"
      colorScheme={isActive ? 'green' : 'red'}
    >
      {isActive ? 'Open' : 'Closed'}
    </Badge>
  )
}

export const AccountTypeCell = ({ value: accountType }: { value: any }) => (
  <span>
    {accountType !== AccountTypeEnum.Dvp
      ? capitalize(accountType)
      : accountType}
  </span>
)

export const NameAddressCell = ({ value }: { value: any }) => {
  return value.map((line: string, index: number) => {
    return <div key={index}>{line}</div>
  })
}
export const PhoneNumbersCell = ({ value }: { value: any }) => {
  return value.map((line: string, index: number) => {
    return <div key={index}>{line}</div>
  })
}

export const AccountNumberLink = ({ value: accountNumber }: { value: any }) => {
  const {
    accountSearchState: { searchTerm = '' },
  } = useAccountSearch()

  return (
    <Link
      as={ReactRouterLink}
      color="apex.link"
      to={{
        pathname: `/${RoutesEnum.AccountSummary}/${accountNumber}`,
        search: `${searchTerm ? `?q=${encodeURIComponent(searchTerm)}` : ''}`,
      }}
    >
      {accountNumber}
    </Link>
  )
}

export interface AccountsTableProps {
  accountResults: Account[]
  height: number
  width: number
}

const AccountsTable = ({
  accountResults,
  height,
  width,
}: AccountsTableProps) => {
  const columns: GridTableColumn[] = [
    assembleColumn(
      'Acct Status',
      'isActive',
      125,
      ACCOUNT_STATUS_OPTIONS,
      AccountStatusCell
    ),
    assembleColumn(
      'Account #',
      'accountNumber',
      125,
      undefined,
      AccountNumberLink
    ),
    assembleColumn('Title', 'accountTitle', 200),
    assembleColumn(
      'Name and Address',
      'nameAndAddress',
      250,
      undefined,
      NameAddressCell
    ),
    assembleColumn(
      'Phone Numbers',
      'phoneNumbers',
      200,
      undefined,
      PhoneNumbersCell
    ),
    assembleColumn('Rep Code', 'repCode', 125),
    assembleColumn('Tax ID', 'last4TIN', 150),
    assembleColumn(
      'Type',
      'accountType',
      125,
      ACCOUNT_TYPE_OPTIONS_EXCEPT_SHORT,
      AccountTypeCell
    ),
  ]

  const records: GridTableRecord[] = accountResults.map(
    ({
      isActive,
      accountNumber,
      accountTitle = '',
      accountNames = [],
      accountAddress = EMPTY_ADDRESS,
      phoneNumbers = [],
      repCode = '',
      last4TIN,
      accountType = '',
    }) => {
      const names = accountNames.join(' ')
      const formattedAddress = formatAddress(accountAddress)
      const nameAndAddress = [names].concat(formattedAddress)

      return {
        key: accountNumber,
        id: uniqueId(),
        isActive: `${isActive === true}`,
        accountNumber,
        accountTitle,
        nameAndAddress,
        phoneNumbers: formatPhoneNumbers(phoneNumbers),
        repCode,
        last4TIN: last4TIN ? `xxx-xx-${last4TIN}` : '',
        accountType,
        accountNumberForLink: accountNumber,
      }
    }
  )

  const paginationProps: PaginationProps = {
    recordsPerPage: [100, 250, 500, 1000],
  }

  return (
    <GridTable
      title="Results"
      columns={columns}
      records={records.reverse()}
      paginationProps={paginationProps}
      height={height}
      width={width}
      paddingBottom={20}
    />
  )
}

export default AccountsTable
